import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "type", "subtitle", "headline", "quizResult",
        "community", "memberCount", "communityDescription", "communityFootnote",
        "payoff1Title", "payoff1Body", "payoff2Title", "payoff2Body",
        "zones", "zone1Label", "zone1Bar", "zone1Caption",
        "zone2Label", "zone2Bar", "zone2Caption"
    ]
    static values = {
        quiz1Response: { type: String, default: '' },
        quiz2Response: { type: String, default: '' },
        quiz3Response: { type: String, default: '' },
        // Set by the onboarding controller when the user supplied their skin
        // type on the picker instead of taking the quiz. Empty means the card
        // renders its quiz reading as before.
        knownSkinType: { type: String, default: '' },
        memberCounts: { type: Object, default: {} }
    }

    initialize() {
        this.quizHeadline = this.headlineElement()?.textContent.trim() || ''
    }

    connect() {
        const onboardingController = this.application.getControllerForElementAndIdentifier(
            document.querySelector('[data-controller="onboarding"]'),
            'onboarding'
        )

        if (onboardingController) {
            this.quiz1ResponseValue = onboardingController.skinTypeQuiz1Value || ''
            this.quiz2ResponseValue = onboardingController.skinTypeQuiz2Value || ''
            this.quiz3ResponseValue = onboardingController.skinTypeQuiz3Value || ''
        }

        this.updateSkinType()
    }

    updateSkinType(event) {
        if (event?.target?.dataset) {
            if (event.target.dataset.skinTypeResultQuiz1ResponseValue) {
                this.quiz1ResponseValue = event.target.dataset.skinTypeResultQuiz1ResponseValue
            }
            if (event.target.dataset.skinTypeResultQuiz2ResponseValue) {
                this.quiz2ResponseValue = event.target.dataset.skinTypeResultQuiz2ResponseValue
            }
            if (event.target.dataset.skinTypeResultQuiz3ResponseValue) {
                this.quiz3ResponseValue = event.target.dataset.skinTypeResultQuiz3ResponseValue
            }
        }

        // A picked skin type gets community reassurance instead of a quiz reading.
        if (this.knownSkinTypeValue) {
            this.renderKnownSkinType(this.knownSkinTypeValue)
            return
        }

        this.setHeadline(this.quizHeadline)
        this.alignHeadline("left")
        this.quizResultTarget.classList.remove("hidden")
        this.communityTarget.classList.add("hidden")

        const responses = [
            this.quiz1ResponseValue,
            this.quiz2ResponseValue,
            this.quiz3ResponseValue
        ].filter(Boolean)

        if (responses.length === 0) {
            this.typeTargets.forEach(target => target.textContent = "Unsure")
            this.toggleDetails(false)
            return
        }

        const counts = responses.reduce((acc, type) => {
            acc[type] = (acc[type] || 0) + 1
            return acc
        }, {})

        const skinType = Object.entries(counts).reduce((a, b) =>
            (b[1] > a[1] ? b : a)
        )[0].toLowerCase()

        this.typeTargets.forEach(target => target.textContent = this.titleize(skinType))

        const profile = this.skinTypeProfiles[skinType]
        if (profile) {
            this.renderProfile(profile)
            this.toggleDetails(true)
        } else {
            this.toggleDetails(false)
        }
    }

    renderKnownSkinType(skinType) {
        const type = skinType.toLowerCase()

        this.setHeadline("You're not alone.")
        this.alignHeadline("center")
        this.typeTargets.forEach(target => target.textContent = this.titleize(type))
        this.quizResultTarget.classList.add("hidden")
        this.communityTarget.classList.remove("hidden")

        const count = this.memberCountsValue[type] || 0
        // Round down so the displayed milestone never overstates membership.
        const milestone = count >= 1000 ? Math.floor(count / 1000) * 1000 : count
        this.memberCountTarget.classList.toggle("hidden", count === 0)
        if (count > 0) this.animateCount(milestone, count >= 1000 ? "+" : "")

        if (count > 0) {
            this.communityDescriptionTarget.innerHTML =
                `SkinSort ${count === 1 ? "member" : "members"} with <span class="font-medium text-warm-gray-800">${type} skin</span>`
        } else {
            this.communityDescriptionTarget.textContent = `Let's build a profile around your ${type} skin.`
        }
        this.communityFootnoteTarget.classList.toggle("hidden", count === 0)

        this.renderPayoff(this.communityPayoffs[type] || this.communityPayoffs.default, type)
    }

    renderPayoff(payoff, type) {
        this.payoff1TitleTarget.textContent = payoff[0].title
        this.payoff1BodyTarget.textContent = payoff[0].body
        this.payoff2TitleTarget.textContent = `Match scores tuned for ${type} skin`
        this.payoff2BodyTarget.textContent = payoff[1].body
    }

    animateCount(target, suffix) {
        const element = this.memberCountTarget
        const format = value => value.toLocaleString("en-US")
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

        if (reduceMotion || target < 1000) {
            element.textContent = format(target) + suffix
            return
        }

        const duration = 700
        let start = null
        const step = timestamp => {
            if (start === null) start = timestamp
            const progress = Math.min(1, (timestamp - start) / duration)
            const eased = 1 - Math.pow(1 - progress, 3)
            const value = Math.round(target * eased / 1000) * 1000
            element.textContent = format(value) + (progress === 1 ? suffix : "")
            if (progress < 1) requestAnimationFrame(step)
        }
        element.textContent = format(0)
        requestAnimationFrame(step)
        // Frames stall in background WebViews; never leave the count at zero.
        setTimeout(() => { element.textContent = format(target) + suffix }, duration + 100)
    }

    alignHeadline(direction) {
        if (!this.hasHeadlineTarget) return

        this.headlineTarget.classList.toggle("text-left", direction === "left")
        this.headlineTarget.classList.toggle("text-center", direction === "center")
    }

    setHeadline(text) {
        if (!text) return

        const element = this.headlineElement()
        if (element) element.textContent = text
    }

    headlineElement() {
        if (!this.hasHeadlineTarget) return null

        return this.headlineTarget.querySelector("h1") || this.headlineTarget
    }

    renderProfile(profile) {
        if (this.hasSubtitleTarget) {
            this.subtitleTarget.textContent = profile.subtitle
        }

        if (this.hasZonesTarget) {
            const [zone1, zone2] = profile.zones
            this.zone1LabelTarget.textContent = zone1.label
            this.zone1BarTarget.style.width = `${zone1.level}%`
            this.zone1CaptionTarget.textContent = zone1.caption
            this.zone2LabelTarget.textContent = zone2.label
            this.zone2BarTarget.style.width = `${zone2.level}%`
            this.zone2CaptionTarget.textContent = zone2.caption
        }
    }

    toggleDetails(visible) {
        const detailTargets = [
            this.hasSubtitleTarget ? this.subtitleTarget : null,
            this.hasZonesTarget ? this.zonesTarget : null
        ].filter(Boolean)

        detailTargets.forEach(target => target.classList.toggle("hidden", !visible))
    }

    get communityPayoffs() {
        return {
            'sensitive': [
                { title: "Irritants flagged on every product", body: "Fragrance, essential oils and common allergens are called out before you buy." },
                { body: "Gentle, low-risk formulas rise to the top of your results from here on." }
            ],
            'oily': [
                { title: "Pore-cloggers flagged on every product", body: "Heavy oils and comedogenic ingredients are called out before you buy." },
                { body: "Lightweight, oil-controlling formulas rise to the top of your results from here on." }
            ],
            'dry': [
                { title: "Drying ingredients flagged on every product", body: "Stripping cleansers and drying alcohols are called out before you buy." },
                { body: "Hydrating, barrier-supporting formulas rise to the top of your results from here on." }
            ],
            'combination': [
                { title: "Balance checked on every product", body: "Ingredients that over-dry your cheeks or add shine to your T-zone are called out before you buy." },
                { body: "Formulas that work for both zones rise to the top of your results from here on." }
            ],
            'normal': [
                { title: "Every product checked against your profile", body: "Irritants and pore-cloggers are called out before you buy." },
                { body: "Well-rounded formulas that keep your skin balanced rise to the top of your results from here on." }
            ],
            'default': [
                { title: "Every product checked against your profile", body: "Ingredients your skin type tends to dislike are called out before you buy." },
                { body: "Formulas suited to your skin type rise to the top of your results from here on." }
            ]
        }
    }

    get skinTypeProfiles() {
        return {
            'oily': {
                subtitle: "More shine across your whole face",
                zones: [
                    { label: "Forehead + nose", level: 82, caption: "More oil" },
                    { label: "Cheeks + jaw", level: 65, caption: "Some oil" }
                ]
            },
            'dry': {
                subtitle: "Less oil, tightness through the day",
                zones: [
                    { label: "Forehead + nose", level: 25, caption: "Drier" },
                    { label: "Cheeks + jaw", level: 18, caption: "Drier" }
                ]
            },
            'combination': {
                subtitle: "Oilier T-zone, drier cheeks",
                zones: [
                    { label: "Forehead + nose", level: 78, caption: "More oil" },
                    { label: "Cheeks + jaw", level: 32, caption: "Drier" }
                ]
            },
            'normal': {
                subtitle: "Balanced oil across your face",
                zones: [
                    { label: "Forehead + nose", level: 52, caption: "Balanced" },
                    { label: "Cheeks + jaw", level: 48, caption: "Balanced" }
                ]
            },
            'sensitive': {
                subtitle: "Prone to reactions and irritation",
                zones: [
                    { label: "Sensitivity", level: 78, caption: "Higher" },
                    { label: "Resilience", level: 30, caption: "Needs support" }
                ]
            }
        }
    }

    titleize(str) {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    }
}
